import { useEffect, useState } from "react";

import { nutritionActions } from "@fitness-app/app-store";
import { type Ingredient } from "@fitness-app/data-models/entities/Ingredient";

import { useEntityChange } from "~/hooks/useEntityChange";
import {
  transformEntityToEditModel,
  transformUpdateModelToEntity,
} from "~/modules/Nutrition/AddIngredientForm/transformModelToEntity";
import { type AddIngredientFormModel } from "~/modules/Nutrition/AddIngredientForm/types";
import { useNutritionMeasures } from "~/modules/Nutrition/hooks/useNutritionMeasures";
import { useAppDispatch } from "~/store/initializeStore";

export const useEditIngredient = (canApprove: boolean) => {
  const [showEditIngredientModal, setShowEditIngredientModal] = useState(false);
  const [editModel, setEditModel] = useState<Ingredient | null>(null);
  const [editFormModel, setEditFormModel] = useState<AddIngredientFormModel | null>(null);
  const [loading, onSuccess, onFailure, onStart] = useEntityChange();
  const dispatch = useAppDispatch();
  const { measuresMap } = useNutritionMeasures();

  useEffect(() => {
    if (editModel) {
      setEditFormModel(transformEntityToEditModel(editModel));
      setShowEditIngredientModal(true);
    }
  }, [editModel]);

  const saveUpdatedIngredient = async (model: AddIngredientFormModel) => {
    if (editModel) {
      onStart();
      try {
        await dispatch(
          nutritionActions.editIngredient({
            ingredient: transformUpdateModelToEntity(model, measuresMap, editModel, canApprove),
          }),
        ).unwrap();
        onSuccess();
        closeEditModal();
      } catch (e) {
        onFailure();
      }
    }
  };

  const closeEditModal = () => {
    setShowEditIngredientModal(false);
    setTimeout(() => {
      setEditModel(null);
      setEditFormModel(null);
    }, 500);
  };

  return {
    showEditIngredientModal,
    setShowEditIngredientModal,
    editModel,
    setEditModel,
    closeEditModal,
    editFormModel,
    saveUpdatedIngredient,
    updating: loading,
  };
};
